import { Fragment, useContext } from "react";
import AccessibleText from "./AccessibleText";
import { Context } from "Context";
import { trackLink } from "utils/analyticsFramework";
import CoBrowse from "./CoBrowse";
import { buildHref } from "utils/link";
import { handleOneTrustModal } from "utils/oneTrust";
import appConfig from "config.json";

const { targetOneId, brands } = appConfig;
const interstitialPrefix = "interstitial";
const coBrowseInterstitial = "interstitialCobrowseModal";
const modalLayer = "modalLayer";
const oneTrust = "#oneTrustModal";
const authenticationHrefMethod = "#navAuthentication-";
const lowerPrefixes = ["development", "latest", "stage", "lt01", "load"];

const buildTarget = (modal) => {
	if (modal.description) {
		modal.description = modal.description.replaceAll(
			'target="_self"',
			'target="_blank"'
		);
	}
};

const handleInterstitial = ({
	e,
	interstitials,
	target,
	href,
	setModal,
	bypassBasePath,
	handleClick,
	bypassHandleClick,
	openLinkContentInNewTab,
	focusElem,
	focusElemScrollY
}) => {
	const modal = interstitials[target];
	if (modal) {
		e.preventDefault();
		if (openLinkContentInNewTab) {
			buildTarget(modal);
		}
		if (!bypassHandleClick) {
			modal.href = href;
		}
		setModal({ ...modal, bypassBasePath, handleContinue: handleClick, focusElem, focusElemScrollY });
	}
};

const handleModalLayer = ({
	e,
	href,
	interstitials,
	setModal,
	openLinkContentInNewTab,
}) => {
	const modalId = href.match(/[^#]+/g)[0];
	const modal = interstitials[modalId];
	if (modal) {
		e.preventDefault();
		if (openLinkContentInNewTab) {
			buildTarget(modal);
		}
		if (modalId === coBrowseInterstitial) {
			setModal({ content: <CoBrowse {...modal} />, preventFocus: true });
		} else {
			setModal({ 
				...modal, 
				href: href.match(/[^#]+/g)[1] || modal.href, 
				focusElem: e.currentTarget, 
				focusElemScrollY: window.scrollY 
			});
		}
	}
};

const handleTriggerAnalytics = ({ e, analyticsLinkId }) => {
	const linkId = `${analyticsLinkId}_${e.type === "click" ? "CLICK" : "HOVER"}`;

	if (analyticsLinkId) {
		trackLink({ linkId });
	}
};

const handleAuthenticationMethod = (e, href) => {
	e.preventDefault();
	document.dispatchEvent(
		new CustomEvent("navigationAuth", {
			detail: {
				method: href.replace(authenticationHrefMethod, ""),
			},
		})
	);
};

const Link = ({
	accessibleText = "",
	analyticsLinkId = null,
	children = null,
	className = null,
	handleClick = null,
	href,
	title = "",
	tabIndex = 0,
	target = "_self",
	rel = null,
	role = null,
	bypassBasePath = false,
	innerHtml = false,
	bypassHandleClick = false,
	openLinkContentInNewTab = false,
	ariaLabel = null,
	collapse = null,
	renderNotLinkTitle = false,
	style = null,
	lang = null,
	focusElem = null,
	focusElemScrollY = null
}) => {
	const {
		headerData: { interstitial: headerInterstitials = {} } = {},
		footerData: { interstitial: footerInterstitials = {} } = {},
		legalFooterData: { interstitial: legalFooterInterstitials = {} } = {},
		setModal,
		config: { basePath, store, brand, environment },
	} = useContext(Context);

	const interstitials = {
		...headerInterstitials,
		...footerInterstitials,
		...legalFooterInterstitials,
	};

	if (!href && !renderNotLinkTitle) {
		console.error("No href present");
		return null;
	}

	if (!title && children === null && !accessibleText && !renderNotLinkTitle) {
		console.error("No title or content present for: ", href);
		return null;
	}

	href = renderNotLinkTitle
		? null
		: buildHref(href, store, basePath, bypassBasePath);

	if (
		lowerPrefixes.includes(environment) &&
		brands[brand].hiddenLinksInLowers?.includes(href)
	) {
		className = `${className} syndicated-link-hidden`;
	}

	const ariaCurrent =
		href === window.location.pathname || href === window.location.href
			? "page"
			: undefined;

	return (
		<a
			href={href}
			lang={lang}
			onClick={(e) => {
				target &&
					target.includes(interstitialPrefix) &&
					handleInterstitial({
						e,
						interstitials,
						target,
						href,
						setModal,
						bypassBasePath,
						handleClick,
						bypassHandleClick,
						openLinkContentInNewTab,
						focusElem,
						focusElemScrollY
					});
				target &&
					target.includes(modalLayer) &&
					handleModalLayer({
						e,
						href,
						interstitials,
						setModal,
						openLinkContentInNewTab,
					});
				href && href.includes(oneTrust) && handleOneTrustModal(e, brand);
				target &&
					target.includes(targetOneId) &&
					href.includes(authenticationHrefMethod) &&
					handleAuthenticationMethod(e, href);
				!bypassHandleClick && handleClick && handleClick(e);
				analyticsLinkId && handleTriggerAnalytics({ e, analyticsLinkId });
			}}
			onMouseEnter={(e) => {
				analyticsLinkId && handleTriggerAnalytics({ e, analyticsLinkId });
			}}
			name={analyticsLinkId ? `&lid=${analyticsLinkId}` : null}
			className={className}
			tabIndex={tabIndex}
			target={target}
			role={role}
			rel={rel}
			aria-label={ariaLabel}
			aria-expanded={collapse}
			style={style}
			aria-current={ariaCurrent}
		>
			<Fragment>
				{children ||
					(innerHtml ? (
						<span dangerouslySetInnerHTML={{ __html: title }} />
					) : (
						title
					))}
				{accessibleText && <AccessibleText>{accessibleText}</AccessibleText>}
			</Fragment>
		</a>
	);
};

export default Link;
